import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CentralServerService } from 'services/central-server.service';
import { DialogService } from 'services/dialog.service';
import { MessageService } from 'services/message.service';
import { SpinnerService } from 'services/spinner.service';
import { ChargingStation, Connector, OCPPGeneralResponse, QrCodeData } from 'types/ChargingStation';
import { ActionResponse } from 'types/DataResult';
import { ButtonAction, FilterParams } from 'types/GlobalType';
import { Transaction } from 'types/Transaction';
import { Utils } from 'utils/Utils';

import { SessionService } from '../service/session.service';

@Component({
  selector: 'app-charging',
  templateUrl: 'charging.component.html',
  styleUrls: ['charging.component.scss']
})
export class ChargingComponent implements OnInit, OnDestroy {
  tagId: string;
  userId: string;
  transactionId: number | null = null;
  transactionDetails = {} as Transaction;
  formattedDuration = '00:00';
  energyInKWh = 0;
  intervalId: any;
  isTransactionStopped = false;
  chargingStationId: string;
  connectorId: number;
  connector: Connector;
  codedChargingStationID: string;
  codedConnectorID: string;
  QrCodeData: QrCodeData | null = null;
  chargingStation: ChargingStation;
  public connectorStatus: string;
  public connectorExists = true;
  transactoinStarted = false;
  isStopRequested = false;
  public loading = false;
  isLoading: boolean;
  walletBalance = 0;
  public tenantLogo: string | null = null;
  public tenantName = '';
  public selectedLanguage = 'fa';
  finalPrice = 0;
  finalDuration = '';
  finalEnergy = 0;
  private token: string;
  private authToken: string;

  constructor(
    private route: ActivatedRoute,
    private centralServerService: CentralServerService,
    private spinnerService: SpinnerService,
    private translateService: TranslateService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private sessionService: SessionService
  ) { }

  get currentPowerInKw(): number {
    return (this.transactionDetails?.currentInstantWatts || 0) / 1000;
  }

  get stationName(): string {
    return this.chargingStation?.siteArea?.name || this.chargingStation?.id || this.chargingStationId || '';
  }

  get stationAddress(): string {
    return this.chargingStation?.site?.address?.address1 || '';
  }

  get connectorLabel(): string {
    if (!this.connector) {
      return `${this.connectorId || ''}`;
    }
    const power = this.connector.power ? ` - ${Math.round(this.connector.power / 1000)} kW` : '';
    return `${this.connector.type}${power}`;
  }

  changeLanguage(lang: string): void {
    this.selectedLanguage = lang;
    this.translateService.use(lang);
    sessionStorage.setItem('qr_selected_language', lang);
    document.documentElement.dir = lang === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }

  public async ngOnInit(): Promise<void> {
    this.route.paramMap.subscribe((params) => {
      this.codedChargingStationID = params.get('codedChargingStationID');
      this.codedConnectorID = params.get('codedConnectorID');
    });
    this.userId = this.sessionService.getUserId() || sessionStorage.getItem('qr_session_userId') || '';
    this.tagId = this.sessionService.getTagId() || '';

    if (this.codedChargingStationID && this.codedConnectorID) {
      try {
        this.QrCodeData = await firstValueFrom(this.centralServerService.getQrCodeData(this.codedChargingStationID, this.codedConnectorID));
        if (this.QrCodeData) {
          this.chargingStationId = this.QrCodeData.chargingStationID;
          this.connectorId = Number(this.QrCodeData.connectorID);
          this.token = this.QrCodeData.token;
          this.tenantName = this.QrCodeData.tenantName || '';
          if (this.tenantName) {
            this.centralServerService.getTenantLogoBySubdomain(this.tenantName).subscribe({
              next: (logo) => this.tenantLogo = logo,
              error: () => this.tenantLogo = null,
            });
          }
          console.log('ChargingComponent ~ ngOnInit ~ this.chargingStationId:', this.chargingStationId);
          console.log('ChargingComponent ~ ngOnInit ~ this.connectorId:', this.connectorId);
        } else {
          this.messageService.showErrorMessage('Failed to load QR code data');
        }
      } catch (error) {
        console.error('Error fetching QR code data:', error);
        this.messageService.showErrorMessage('Error loading payment data');
      }
    } else {
      console.error('codedChargingStationID or codedConnectorID is missing');
      this.messageService.showErrorMessage('Invalid charging station or connector ID');
    }

    const language = sessionStorage.getItem('qr_selected_language') || this.translateService.currentLang || 'fr';
    const supportedLanguages = ['en', 'fr', 'fa'];
    const languageToUse = supportedLanguages.includes(language) ? language : 'fa';
    this.translateService.use(languageToUse);
    this.selectedLanguage = languageToUse;
    document.documentElement.dir = languageToUse === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.lang = languageToUse;

    this.chargingStation = await firstValueFrom(this.centralServerService.getChargingStation(this.chargingStationId));
    this.connector = Utils.getConnectorFromID(this.chargingStation, this.connectorId);
    await this.loadWalletBalance();

    this.intervalId = setInterval(async () => {
      if (this.transactionId) {
        this.isLoading = true;
        try {
          await this.loadChargingStation(this.chargingStationId, this.connectorId);
          this.getTransactionDetails();
          await this.loadWalletBalance();
        } catch (error) {
          console.error('Error during interval loading:', error);
        } finally {
          this.isLoading = false;
        }
      }
    }, 10000);
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  redirectToEv24(): void {
    window.location.href = 'https://www.ev24.io/support/';
  }

  public stopTransaction(dialogService: DialogService, translateService: TranslateService): void {
    dialogService.createAndShowYesNoDialog(
      translateService.instant('stop_transaction_title'),
      translateService.instant('stop_transaction_confirm', { chargeBoxID: this.chargingStation.id, transactionId: this.transactionId })
    ).subscribe((response) => {
      if (response === ButtonAction.YES) {
        this.spinnerService.show();
        this.centralServerService.stopTransaction(this.transactionId).subscribe({
          next: (res: ActionResponse) => {
            this.spinnerService.hide();
            if (res.status === OCPPGeneralResponse.ACCEPTED) {
              console.log(`Transaction ${this.transactionId} stop requested successfully.`);
              this.isStopRequested = true;
              this.getTransactionDetails();
            } else {
              console.error('Failed to stop transaction');
            }
          },
          error: (error) => {
            this.spinnerService.hide();
            console.error('Error stopping transaction:', error);
          }
        });
      }
    });
  }

  public async getUserTransactions(): Promise<number | null> {
    try {
      const filterParams: FilterParams = {
        UserID: this.userId, Issuer: 'true', WithCompany: 'true', WithSite: 'true', WithSiteArea: 'true',
        WithTag: 'true', WithUser: 'true', WithCar: 'true', WithChargingStation: 'true', Statistics: 'ongoing',
        Limit: '50', SortFields: '-timestamp'
      };
      console.log('ChargingComponent ~ getUserTransactions ~ userId:', this.userId);
      const transactionResult = await firstValueFrom(this.centralServerService.getActiveTransactions(filterParams));
      if (transactionResult.result.length > 0) {
        console.log('Transaction ID retrieved:', transactionResult.result[0].id);
        return transactionResult.result[0].id;
      }
      console.log('No active transactions found for user');
      return null;
    } catch (err) {
      console.error('Error retrieving transactions:', err);
      return null;
    }
  }

  public getTransactionDetails(): void {
    this.centralServerService.getQRCodeTransaction(this.transactionId).subscribe({
      next: (transaction) => {
        this.transactionDetails = transaction;
        console.log('ChargingComponent ~ getQRCodeTransaction ~ this.transactionDetails:', this.transactionDetails);
        this.formattedDuration = Utils.convertSecondsToMinutesAndSeconds(transaction.currentTotalDurationSecs);
        this.energyInKWh = Utils.convertWattsToKWh(transaction.currentTotalConsumptionWh);
        if (transaction.stop) {
          this.completeTransaction(transaction);
        }
      },
      error: (error) => {
        console.error('Error fetching transaction details:', error);
      }
    });
  }

  public async startTransaction(): Promise<void> {
    if (!await this.refreshAndCheckConnectorStatus()) {
      const msg = this.translateService.instant('qrcode.connector_not_ready_redirect');
      this.messageService.showErrorMessage(msg !== 'qrcode.connector_not_ready_redirect' ? msg : 'Le connecteur n\'est pas prêt. Veuillez brancher votre véhicule et réessayer.');
      return;
    }
    try {
      console.log('Starting transaction...');
      this.transactoinStarted = true;
      if (!this.tagId && this.userId) {
        this.tagId = await this.assignTagToUser(this.userId);
        this.sessionService.setTagId(this.tagId);
      }
      const response = await this.centralServerService.startTransaction(this.chargingStationId, this.connectorId, this.userId, this.tagId).toPromise();
      if (response.status === OCPPGeneralResponse.ACCEPTED) {
        console.log('Transaction started successfully');
        await new Promise(resolve => setTimeout(resolve, 3000));
        let retryCount = 0;
        const maxRetries = 15;
        while (retryCount < maxRetries) {
          this.transactionId = await this.getUserTransactions();
          if (this.transactionId) {
            break;
          }
          retryCount++;
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        if (this.transactionId) {
          this.getTransactionDetails();
        } else {
          console.error('Transaction ID is null or undefined after retries');
        }
      } else {
        console.error('Failed to start transaction');
      }
    } catch (error) {
      console.error('Error starting transaction:', error);
    } finally {
      if (!this.transactionId) {
        this.transactoinStarted = false;
      }
      this.spinnerService.hide();
    }
  }

  public async loadWalletBalance(): Promise<void> {
    try {
      if (this.userId) {
        const wallet = await firstValueFrom(this.centralServerService.getQRCodeWalletBalance(this.userId));
        this.walletBalance = wallet?.credit || 0;
      }
    } catch (error) {
      console.error('Error loading wallet:', error);
    }
  }

  public formatAmount(amount: number | null | undefined): string {
    const value = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 3 }).format(value);
  }

  private completeTransaction(transaction: Transaction): void {
    const stop = transaction.stop;
    if (!stop || this.isTransactionStopped) {
      return;
    }
    this.finalPrice = stop.price ?? transaction.price ?? 0;
    this.finalDuration = Utils.convertSecondsToMinutesAndSeconds(stop.totalDurationSecs ?? transaction.currentTotalDurationSecs ?? 0);
    this.finalEnergy = Utils.convertWattsToKWh(stop.totalConsumptionWh ?? transaction.currentTotalConsumptionWh ?? 0);
    this.isTransactionStopped = true;
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.messageService.showInfoMessage(
      this.translateService.instant('chargers.transaction_stopped'),
      this.translateService.instant('chargers.please_unplug_connector')
    );
  }

  private async assignTagToUser(userId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const tagID = Utils.generateTagID();
      const tag = { id: tagID, default: true, active: true, issuer: true, userID: userId, visualID: tagID, description: 'Auto-generated tag', emspName: '' };
      this.centralServerService.createQrTag(tag).subscribe({
        next: () => resolve(tagID),
        error: (error) => reject(error)
      });
    });
  }

  private async refreshAndCheckConnectorStatus(): Promise<boolean> {
    try {
      const station = await firstValueFrom(this.centralServerService.getChargingStationQr(this.chargingStationId));
      this.chargingStation = station;
      this.connector = Utils.getConnectorFromID(station, this.connectorId);
      const status = this.connector?.status;
      if (status !== 'Preparing') {
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error refreshing connector status:', error);
      return false;
    }
  }

  private async loadChargingStation(entityId: string, connectorId: number): Promise<void> {
    this.isLoading = true;
    return new Promise<void>((resolve, reject) => {
      this.centralServerService.getChargingStation(entityId).subscribe({
        next: (chargingStation: ChargingStation) => {
          this.chargingStation = chargingStation;
          this.connectorExists = chargingStation.connectors.some((connector) => connector.connectorId === connectorId);
          this.connectorId = connectorId;
          this.connector = Utils.getConnectorFromID(chargingStation, connectorId);
          if (this.connector) {
            const statusKey = `connectorStatus.${this.connector.status.toUpperCase()}`;
            this.translateService.get(statusKey).subscribe((translatedStatus: string) => {
              this.connectorStatus = translatedStatus || this.connector.status;
              this.connectorExists = true;
              this.spinnerService.hide();
              resolve();
            });
          } else {
            this.connectorExists = false;
            this.connectorStatus = '';
            this.spinnerService.hide();
            resolve();
          }
        },
        error: (error) => {
          console.error('Failed to load charging station:', error);
          this.spinnerService.hide();
          this.connectorExists = false;
          this.connectorStatus = '';
          reject(error);
        },
      });
    }).finally(() => {
      this.isLoading = false;
    });
  }

}
